import {
   productCodeToCalculatedValuesPriceObject,
   recalculateWithoutDiscount,
   getProrationDetails,
} from './calculate.mjs'
import { findObjectByKeyValue, getAccountSnowflakeQtyItem } from './helpers.mjs'

/**
 * Calculates the total activation price for a given monthly product over a specified term.
 *
 * @param {any} monthlyProduct - Specific product object to recalculate. Typically contains product pricing and appliedDiscounts.
 * @param {any[]} product_list - Array of available products.
 * @param {Record<string, any>} discount_list - Object mapping discount identifiers to their details.
 * @param {string|number} book_id - Identifier for the book in the system.
 * @param {string} quoteType - The type of quote being calculated.
 * @param {boolean} coreProductSelected - Flag indicating if the core product is selected.
 * @param {number} productCoreEECount - Core employee count for the product.
 * @param {number} productPerformanceEECount - Performance employee count for the product.
 * @param {number} productTimeTrackingEECount - Time tracking employee count for the product.
 * @param {boolean} hasTimeClocks - Flag indicating if time clocks are enabled.
 * @param {any} productTimeClockDetails - Details for time clock configuration.
 * @param {number} productBenefitsAdministrationEECount - Benefits administration employee count.
 * @param {number} productPayrollEINCount - Payroll EIN count.
 * @param {number} productPayrollEECount - Payroll employee count.
 * @param {any} productPayrollEECounts - Additional payroll employee counts (complex structure).
 * @param {number} productPayrollTotalStateCount - Total state count for payroll.
 * @param {any} productPayrollStateCounts - Payroll state counts (array or object).
 * @param {any} professionalServiceDetails - Details for professional services.
 * @param {any} consultingServiceDetails - Details for consulting services.
 * @param {string} impType - Implementation type for the product.
 * @param {string} benAdminImpType - Benefits administration implementation type.
 * @param {string} payrollImpType - Payroll implementation type.
 * @param {any} discountBundle - Bundle discount details.
 * @param {boolean} discountNonProfit - Indicates if a non-profit discount applies.
 * @param {number} prepayLength - Prepayment length in months (1 = month-to-month).
 * @param {boolean} discountMultiEntity - Indicates if a multi-entity discount applies.
 * @param {boolean} discountHRMonthlyPromotion - Indicates if HR monthly promotion discount applies.
 * @param {boolean} discountHRDealDeskMonthly - Indicates if HR deal desk monthly discount applies.
 * @param {boolean} discountReseller - Indicates if a reseller discount applies.
 * @param {boolean} discountQuickstartTraining - Indicates if a quickstart training discount applies.
 * @param {boolean} discountImpPromotion - Indicates if an implementation promotion discount applies.
 * @param {boolean} discountDealDeskHRImp - Indicates if deal desk HR implementation discount applies.
 * @param {boolean} discountDealDeskPayrollImp - Indicates if deal desk payroll implementation discount applies.
 * @param {boolean} discountSecondEIN - Indicates if a second EIN discount applies.
 * @param {boolean} discountGroupImp - Indicates if a group implementation discount applies.
 * @param {number} discountDealDeskHRImpRate - Rate for deal desk HR implementation discount.
 * @param {number} discountDealDeskQuickstartImpRate - Rate for deal desk quickstart implementation discount.
 * @param {number} discountDealDeskPayrollImpRate - Rate for deal desk payroll implementation discount.
 * @param {number} productAdditionalStorageCount - Additional storage count for the product.
 * @param {number} productAdditionalJobsCount - Additional jobs count for the product.
 * @param {boolean} productHistoricalDataImport - Flag indicating if historical data import is included.
 * @param {boolean} productMarketplaceListing - Flag for marketplace listing inclusion.
 * @param {boolean} productSponsorship - Flag indicating if sponsorship is included.
 * @param {number} productPaidServiceAmount - Amount for any paid services.
 * @param {boolean} productTestAccount - Flag indicating if this is a test account.
 * @param {Date|string} activationDate - Activation date (defaults to the first day of the month if not supplied).
 * @param {boolean} differenceQuote - Flag indicating if the quote is a difference quote.
 * @param {Record<string, any>} accountSnowflake - Account details from Snowflake; used for additional customer context.
 * @param {number} coreCountryCount - Count of core countries applicable.
 * @param {any} promotionalDiscounts - Promotional discounts applied (object or array).
 * @param {Date|string} nextInvoiceDate - Date of the next invoice.
 * @param {Record<string, any>} currencyObject - Current currency details.
 * @param {number} accountBalance - Account balance in the applicable currency.
 * @param {number} monthlyTax - Monthly tax amount.
 * @param {number} productEINIntegrations - Count of EIN integrations.
 * @param {any} allPermissions - All permission settings for the account (object or array).
 * @param {string} user_role - Role of the current user.
 * @param {string} account_status - Status of the account.
 * @param {Record<string, any>} pricebookDetails - Details for the selected pricebook.
 * @param {Record<string, any>} previousCurrencyObject - Previous currency details.
 * @param {any} productPayrollEINImpTypes - Implementation types for payroll EINs.
 * @param {boolean} prepaymentActivationUseDifference - Flag that determines which calculation (totalPrice vs. totalPriceDifference) is used for the duration of any prepayment term.
 * @param {Map<string, number|boolean>} dynamicProducts - Map of dynamic product codes to their selected values/quantities.
 * @param {string} [selectedTimeProduct='timeTracking'] - Which time product is selected ('timeTracking' or 'timeAttendance').
 * @param {boolean} [isATSOnly=false] - Flag indicating the quote is an ATS-only quote. Propagated through the calculation chain (currently part of the cache key and returned in calculatedValues).
 * @param {number|null} [monthsToCalculate=null] - Optional override for the number of months to calculate. When null, falls back to prepayLength.
 * @param {boolean} [includeProrated=true] - When true, the prorated activation month is included in the total when proration applies.
 * @param {boolean} [forceIncludeBundle=false] - When true, the bundle discount is kept in the calculation even when the standard rules would exclude it.
 *
 * @returns {number} The total activation price for the product over the specified term.
 */
export function calculateActivationTotalForProduct(
   // Specific product to recalculate.
   monthlyProduct,
   // Required inputs to perform a calculation.
   product_list,
   discount_list,
   book_id,
   quoteType,
   coreProductSelected,
   productCoreEECount,
   productPerformanceEECount,
   productTimeTrackingEECount,
   hasTimeClocks,
   productTimeClockDetails,
   productBenefitsAdministrationEECount,
   productPayrollEINCount,
   productPayrollEECount,
   productPayrollEECounts,
   productPayrollTotalStateCount,
   productPayrollStateCounts,
   professionalServiceDetails,
   consultingServiceDetails,
   impType,
   benAdminImpType,
   payrollImpType,
   discountBundle,
   discountNonProfit,
   prepayLength,
   discountMultiEntity,
   discountHRMonthlyPromotion,
   discountHRDealDeskMonthly,
   discountReseller,
   discountQuickstartTraining,
   discountImpPromotion,
   discountDealDeskHRImp,
   discountDealDeskPayrollImp,
   discountSecondEIN,
   discountGroupImp,
   discountDealDeskHRImpRate,
   discountDealDeskQuickstartImpRate,
   discountDealDeskPayrollImpRate,
   productAdditionalStorageCount,
   productAdditionalJobsCount,
   productHistoricalDataImport,
   productMarketplaceListing,
   productSponsorship,
   productPaidServiceAmount,
   productTestAccount,
   activationDate, // if not supplied, defaults to 1st day of month
   differenceQuote,
   accountSnowflake,
   coreCountryCount,
   promotionalDiscounts,
   nextInvoiceDate,
   currencyObject,
   accountBalance,
   monthlyTax,
   productEINIntegrations,
   allPermissions,
   user_role,
   account_status,
   pricebookDetails,
   previousCurrencyObject,
   productPayrollEINImpTypes,
   prepaymentActivationUseDifference,
   dynamicProducts,
   selectedTimeProduct = 'timeTracking',
   isATSOnly = false,
   monthsToCalculate = null,
   includeProrated = true,
   forceIncludeBundle = false,
) {
   if (!monthlyProduct) {
      return 0
   }

   // One-time products are charged exactly once regardless of prepayment term length —
   // looping over the full term would sum the same flat fee once per month.
   const isOneTimeProduct =
      product_list[monthlyProduct.productCode]?.BillingFreq === 'once'

   // Use monthsToCalculate if provided, otherwise fall back to prepayLength (already a number), with default of 1
   const monthsSelected = isOneTimeProduct
      ? 1
      : monthsToCalculate ?? (typeof prepayLength === 'number' ? prepayLength : 1)

   // Extract prorated details from calculatedValues.
   // Pass differenceQuote and account_status so proration only applies when differenceQuote is true for existing accounts
   const proratedDetails = getProrationDetails(activationDate, nextInvoiceDate, differenceQuote, account_status)
   const proratedFraction = proratedDetails?.prorated
      ? proratedDetails.daysToPayAfterActivation /
        proratedDetails.totalDaysInMonth
      : 1

   //Use this as the initial month
   let initialMonth = 1
   let lastMonth = monthsSelected
   //If prorated, start with the amount for the prorated month
   if (proratedDetails.prorated && includeProrated) {
      initialMonth = 0
      //If only prorated portion, only calculate for month 0
      if (monthsSelected === 1) {
         lastMonth = 0
      }
   }
   const discounts =
      monthlyProduct?.appliedDiscounts.map((discount) => ({
         ...discount,
         StartMonth: discount.StartMonth ?? initialMonth,
         EndMonth: discount.EndMonth ?? lastMonth,
      })) ?? []
   const discountCodes = new Set(
      discounts.map((discount) => discount.DiscountCode),
   )
   let discountsToIgnoreEachMonth = []

   for (let i = initialMonth; i <= lastMonth; i++) {
      discountsToIgnoreEachMonth.push(
         new Set(discounts.map((discount) => discount.DiscountCode)),
      )
   }
   //If the customer is not a current payroll customer, we exclude the bundle discount from the calculation

   const isCurrentPayrollCustomer =
      (accountSnowflake.hasOwnProperty('Billed_Payroll__c') &&
         accountSnowflake.Billed_Payroll__c &&
         accountSnowflake.Billed_Payroll__c > 0) ||
      (accountSnowflake.hasOwnProperty('LMI_Payroll_MRR__c') &&
         accountSnowflake.LMI_Payroll_MRR__c &&
         accountSnowflake.LMI_Payroll_MRR__c > 0)

   const shouldIgnoreBundleDiscount =
      discount_list?.['bundleDiscount']?.ApplyTo?.includes(
         monthlyProduct?.productCode,
      ) && !isCurrentPayrollCustomer

   for (const discount of discounts) {
      if (
         discount.DiscountCode === 'bundleDiscount' &&
         shouldIgnoreBundleDiscount &&
         !forceIncludeBundle
      ) {
         // If the bundle discount is applied, we need to ignore it for the calculation
         continue
      }
      for (let i = discount.StartMonth; i <= discount.EndMonth; i++) {
         if (discountCodes.has(discount.DiscountCode)) {
            discountsToIgnoreEachMonth[i - initialMonth]?.delete(
               discount.DiscountCode,
            )
         }
      }
   }

   let totalPrice = 0
   //Define the monthly Price we will use for each month of the term
   let monthlyPrice = 0
   for (let i = initialMonth; i <= lastMonth; i++) {
      let newRecalculatedValues = recalculateWithoutDiscount(
         discountsToIgnoreEachMonth[i - initialMonth],
         product_list,
         discount_list,
         book_id,
         quoteType,
         coreProductSelected,
         productCoreEECount,
         productPerformanceEECount,
         productTimeTrackingEECount,
         hasTimeClocks,
         productTimeClockDetails,
         productBenefitsAdministrationEECount,
         productPayrollEINCount,
         productPayrollEECount,
         productPayrollEECounts,
         productPayrollTotalStateCount,
         productPayrollStateCounts,
         professionalServiceDetails,
         consultingServiceDetails,
         impType,
         benAdminImpType,
         payrollImpType,
         discountBundle,
         discountNonProfit,
         prepayLength,
         discountMultiEntity,
         discountHRMonthlyPromotion,
         discountHRDealDeskMonthly,
         discountReseller,
         discountQuickstartTraining,
         discountImpPromotion,
         discountDealDeskHRImp,
         discountDealDeskPayrollImp,
         discountSecondEIN,
         discountGroupImp,
         discountDealDeskHRImpRate,
         discountDealDeskQuickstartImpRate,
         discountDealDeskPayrollImpRate,
         productAdditionalStorageCount,
         productAdditionalJobsCount,
         productHistoricalDataImport,
         productMarketplaceListing,
         productSponsorship,
         productPaidServiceAmount,
         productTestAccount,
         activationDate, // if not supplied, defaults to 1st day of month
         differenceQuote,
         accountSnowflake,
         coreCountryCount,
         promotionalDiscounts,
         nextInvoiceDate,
         currencyObject,
         accountBalance,
         monthlyTax,
         productEINIntegrations,
         allPermissions,
         user_role,
         account_status,
         pricebookDetails,
         previousCurrencyObject,
         productPayrollEINImpTypes,
         prepaymentActivationUseDifference,
         dynamicProducts,
         selectedTimeProduct,
         isATSOnly,
         true, // isRecalculation: this call is always in a recalculation context
      )
      // Determine if we should use difference pricing for this month
      // Use difference only for month 0 (prorated), OR for all months if prepaymentActivationUseDifference is checked
      const useDifferenceForThisMonth = differenceQuote && (i === 0 || prepaymentActivationUseDifference);

      if (useDifferenceForThisMonth) {
         monthlyPrice = calculateActivationForDiff(newRecalculatedValues, monthlyProduct, accountSnowflake);
      } else {
         monthlyPrice = productCodeToCalculatedValuesPriceObject(newRecalculatedValues, monthlyProduct.productCode)?.totalPrice ?? 0;
      }
      if (proratedDetails.prorated && i === 0) {
         // If the product is monthly
         if (
            product_list[monthlyProduct.productCode]?.BillingFreq === 'monthly'
         ) {
            // If the product is prorated and the product is in the pricebook.
            if (
               product_list[monthlyProduct.productCode]?.IsProrated &&
               !product_list[monthlyProduct.productCode]?.NotInPricebook
            ) {
               monthlyPrice *= proratedFraction
            }
         }
      }
      //Ensure we never give a credit for a month or difference amount
      totalPrice += Math.max(monthlyPrice, 0)
   }
   return totalPrice
}



/**
 * Calculates the activation total for the given product if the quote is a difference quote.
 *
 * @param {CalculatedValues} newRecalculatedValues
 * @param {MonthlyProduct} monthlyProduct
 * @param {AccountSnowflake} accountSnowflake
 * @returns
 */
function calculateActivationForDiff( newRecalculatedValues, monthlyProduct, accountSnowflake) {
   const MinCountCutoff = 25; // 25 Employees
   const MinPercentCutoff = 10; // 10%

    const productObject = newRecalculatedValues?.selectedProductCosts[monthlyProduct.productCode]
    const prevCount = getAccountSnowflakeQtyItem(accountSnowflake, monthlyProduct.productCode);

    if (!prevCount || prevCount === 0 || prevCount === null) {
        return productObject?.totalPriceDifference ?? 0;
    }

    const diffEECount = (productObject?.productEECount ?? 0) - (prevCount ?? 0);
    const percentDiff = (((productObject?.productEECount ?? 0) / (prevCount ?? 1)) - 1) * 100;

    if (diffEECount >= MinCountCutoff && percentDiff >= MinPercentCutoff) {
        return productObject?.totalPriceDifference ?? 0;
    }

    return 0;
}